import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import { ROLES, ROLE_LABELS } from '../../config/constants';
import { getUsersByRole } from '../../services/userService';
import { getConsultancies, createConsultancy } from '../../services/consultancyService';

const getSchema = () =>
  yup.object().shape({
    name: yup.string().required('Name is required').min(2, 'Min 2 characters'),
    email: yup.string().required('Email is required (user will sign in with this Google email)').email('Invalid email'),
    phone: yup.string().matches(/^[0-9]{10}$/, { message: 'Must be a valid 10-digit phone number', excludeEmptyString: true }),
    role: yup.string().required('Role is required'),
    managerId: yup.string().when('role', {
      is: ROLES.ASSOCIATE,
      then: (schema) => schema.required('Manager is required for associate'),
      otherwise: (schema) => schema.notRequired(),
    }),
    bankName: yup.string().when('role', {
      is: ROLES.BANKER,
      then: (schema) => schema.required('Bank name is required for banker'),
      otherwise: (schema) => schema.notRequired(),
    }),
    bankBranch: yup.string().when('role', {
      is: ROLES.BANKER,
      then: (schema) => schema.required('Bank branch is required for banker'),
      otherwise: (schema) => schema.notRequired(),
    }),
    consultancyId: yup.string().when('role', {
      is: ROLES.CONSULTANCY,
      then: (schema) => schema.required('Consultancy is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

const emptyNewConsultancy = {
  name: '', contactPerson: '', phone: '', email: '', address: '',
  accountHolderName: '', accountNumber: '', ifscCode: '', bankName: '', upiVpaId: '',
};

const UserForm = ({ defaultValues = {}, onSubmit, loading = false, isEdit = false }) => {
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [consultancies, setConsultancies] = useState([]);
  const [loadingConsultancies, setLoadingConsultancies] = useState(false);
  const [addConsultancyOpen, setAddConsultancyOpen] = useState(false);
  const [newConsultancy, setNewConsultancy] = useState({ ...emptyNewConsultancy });
  const [savingConsultancy, setSavingConsultancy] = useState(false);
  const [consultancyError, setConsultancyError] = useState('');

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: '',
      managerId: '',
      bankName: '',
      bankBranch: '',
      consultancyId: '',
      ...defaultValues,
    },
    resolver: yupResolver(getSchema()),
  });

  const selectedRole = watch('role');

  // Fetch managers when role is associate
  useEffect(() => {
    const fetchManagers = async () => {
      if (selectedRole === ROLES.ASSOCIATE) {
        try {
          setLoadingManagers(true);
          const { data } = await getUsersByRole(ROLES.MANAGER);
          setManagers(data.data?.users || []);
        } catch (err) {
          console.error('Failed to fetch managers:', err);
        } finally {
          setLoadingManagers(false);
        }
      }
    };

    fetchManagers();
  }, [selectedRole]);

  // Fetch consultancies when role is consultancy
  useEffect(() => {
    const fetchConsultancies = async () => {
      if (selectedRole === ROLES.CONSULTANCY) {
        try {
          setLoadingConsultancies(true);
          const res = await getConsultancies();
          setConsultancies(res.data?.data?.consultancies || []);
        } catch (err) {
          console.error('Failed to fetch consultancies:', err);
        } finally {
          setLoadingConsultancies(false);
        }
      }
    };

    fetchConsultancies();
  }, [selectedRole]);

  const handleAddConsultancySubmit = async () => {
    if (!newConsultancy.name.trim()) {
      setConsultancyError('Consultancy name is required');
      return;
    }
    try {
      setSavingConsultancy(true);
      setConsultancyError('');
      const payload = {};
      Object.entries(newConsultancy).forEach(([k, v]) => {
        const trimmed = v.trim();
        if (trimmed) payload[k] = k === 'ifscCode' ? trimmed.toUpperCase() : trimmed;
      });
      const res = await createConsultancy(payload);
      const created = res.data?.data?.consultancy || res.data?.data;
      setConsultancies((prev) => [...prev, created]);
      setValue('consultancyId', created._id);
      setAddConsultancyOpen(false);
      setNewConsultancy({ ...emptyNewConsultancy });
    } catch (err) {
      setConsultancyError(err.response?.data?.message || 'Failed to create consultancy');
    } finally {
      setSavingConsultancy(false);
    }
  };

  const handleFormSubmit = (formData) => {
    // Clean up fields not relevant to the role
    const cleaned = { ...formData };
    if (cleaned.role !== ROLES.ASSOCIATE) {
      delete cleaned.managerId;
    }
    if (cleaned.role !== ROLES.BANKER) {
      delete cleaned.bankName;
      delete cleaned.bankBranch;
    }
    if (cleaned.role !== ROLES.CONSULTANCY) {
      delete cleaned.consultancyId;
    }
    onSubmit(cleaned);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Grid container spacing={2}>
        {/* Name */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Full Name"
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>

        {/* Email */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email"
                type="email"
                size="small"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={false}
              />
            )}
          />
        </Grid>

        {/* Phone */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Phone"
                size="small"
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            )}
          />
        </Grid>

        {/* Role */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select {...field} label="Role">
                  {Object.values(ROLES)
                    .filter((r) => r !== ROLES.ADMIN)
                    .map((role) => (
                    <MenuItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && (
                  <FormHelperText>{errors.role.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Manager - for Associate role */}
        {selectedRole === ROLES.ASSOCIATE && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="managerId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.managerId}>
                  <InputLabel>Manager</InputLabel>
                  <Select
                    {...field}
                    label="Manager"
                    disabled={loadingManagers}
                  >
                    {managers.map((mgr) => (
                      <MenuItem key={mgr._id} value={mgr._id}>
                        {mgr.name} ({mgr.email})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.managerId && (
                    <FormHelperText>{errors.managerId.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}

        {/* Bank Name - for Banker role */}
        {selectedRole === ROLES.BANKER && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="bankName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Bank Name"
                  size="small"
                  error={!!errors.bankName}
                  helperText={errors.bankName?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* Bank Branch - for Banker role */}
        {selectedRole === ROLES.BANKER && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="bankBranch"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Bank Branch"
                  size="small"
                  error={!!errors.bankBranch}
                  helperText={errors.bankBranch?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* Consultancy - for Consultancy role */}
        {selectedRole === ROLES.CONSULTANCY && (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                {loadingConsultancies ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading consultancies...</Typography>
                  </Box>
                ) : consultancies.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    No consultancies added yet. Use the button to add one.
                  </Alert>
                ) : (
                  <Controller
                    name="consultancyId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.consultancyId}>
                        <InputLabel>Consultancy</InputLabel>
                        <Select {...field} label="Consultancy">
                          {consultancies.map((c) => (
                            <MenuItem key={c._id} value={c._id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.consultancyId && (
                          <FormHelperText>{errors.consultancyId.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setConsultancyError(''); setAddConsultancyOpen(true); }}
                sx={{ mt: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Add New
              </Button>
            </Box>
          </Grid>
        )}

        {/* Add New Consultancy Dialog */}
        <Dialog
          open={addConsultancyOpen}
          onClose={() => { setAddConsultancyOpen(false); setNewConsultancy({ ...emptyNewConsultancy }); setConsultancyError(''); }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Add New Consultancy</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {consultancyError && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">{consultancyError}</Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Consultancy Name"
                  required
                  autoFocus
                  value={newConsultancy.name}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={newConsultancy.contactPerson}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, contactPerson: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={newConsultancy.phone}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newConsultancy.email}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={newConsultancy.address}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, address: e.target.value }))}
                />
              </Grid>

              {/* Banking Details */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceOutlined sx={{ fontSize: 20 }} /> Banking Details
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={newConsultancy.accountHolderName}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, accountHolderName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={newConsultancy.accountNumber}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, accountNumber: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={newConsultancy.ifscCode}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={newConsultancy.bankName}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, bankName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="UPI VPA ID"
                  placeholder="e.g. name@upi"
                  value={newConsultancy.upiVpaId}
                  onChange={(e) => setNewConsultancy((prev) => ({ ...prev, upiVpaId: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => { setAddConsultancyOpen(false); setNewConsultancy({ ...emptyNewConsultancy }); setConsultancyError(''); }}
              color="inherit"
              disabled={savingConsultancy}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddConsultancySubmit}
              disabled={savingConsultancy || !newConsultancy.name.trim()}
            >
              {savingConsultancy ? 'Saving...' : 'Add Consultancy'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit */}
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ mt: 1, minWidth: 140 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEdit ? (
              'Update User'
            ) : (
              'Create User'
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserForm;
